import React, { useState, useEffect, useRef, useMemo } from "react";
import { TextField, CircularProgress, Box, Alert, Tooltip, IconButton, Button, MenuItem, Select, InputLabel, FormControl, Card, CardContent, Typography, Tabs, Tab, Switch } from "@mui/material";
import Datagrid from "../../components/Datagrid/Datagrid";
import DatagridLoading from "../../components/DatagridLoading/DatagridLoading";
import { useSite } from "../../context/siteContext";
import { useOptions } from "../../context/optionContext";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import Swal from "sweetalert2";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { ALERT_MESSAGES, BUTTON, LABELS, UI_TEXT } from "../../constants";
import { useTranslation } from "react-i18next";
import { styled, autocompleteClasses } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { ListSubheader } from "@mui/material";
import ModalWrapper from "../../components/ModalWrapper/ModalWrapper";
import {
	getCustomSitesData,
	updateCustomerSites,
	getSiteAddresses,
	createSiteAddress,
	updateSiteAddress,
	deleteSiteAddress,
	getUserAllocations,
} from "../../services/manageSitesService";
import { toast } from "../../context/ToastContext";
import { getAllUsers } from "../../services/analyticsService";
import { getCustomerLocationOptionFlags, resolveServiceOptions } from "../../utilities/customerLocationSettings";
import { useAuth } from "../../context/authContext";

const GroupHeader = styled("div")(() => ({
	position: "sticky",
	top: 0,
	padding: "4px 8px",
	color: "#181818",
	backgroundColor: "#eee",
	fontWeight: 600,
	fontSize: "12.5px",
}));

const GroupItems = styled("ul")({
	padding: 0,
	margin: 0,
	listStyle: "none",
});

const getBooleanOption = (value) => {
	if (typeof value === "boolean") return value;
	if (typeof value === "number") return value === 1;
	if (typeof value === "string") {
		const normalized = value.trim().toLowerCase();
		return ["true", "1", "enabled", "yes"].includes(normalized);
	}
	return false;
};

const normalizeUsersResponse = (resp) => {
	const payload = resp?.data?.data ?? resp?.data ?? [];
	return Array.isArray(payload) ? payload : [];
};

const filterUsersForSite = (users = [], siteId) => {
	const normalizedId = Number(siteId);
	return users.filter((user) => {
		if (!user || user.user_id === 1) return false;
		const roleName = `${user?.user_role_name || ""}`.trim().toLowerCase();
		const isAdmin = roleName === "admin";
		const allocations = Array.isArray(user?.allocations) ? user.allocations : [];
		const allocated = allocations.some((allocation) => Number(allocation.site_id) === normalizedId);
		return isAdmin || allocated;
	});
};

const withUserCounts = (sitesList = [], usersList = []) => {
	if (!Array.isArray(sitesList)) return [];
	const counts = sitesList.reduce((acc, site) => {
		acc[Number(site.site_id)] = filterUsersForSite(usersList, site.site_id).length;
		return acc;
	}, {});
	return sitesList.map((site) => ({
		...site,
		user_count: counts[Number(site.site_id)] || 0,
	}));
};

const normalizeCustomQueryResponse = (payload) => {
	if (!payload) return [];
	if (Array.isArray(payload)) return payload;
	if (Array.isArray(payload?.data)) return payload.data;
	if (Array.isArray(payload?.data?.data)) return payload.data.data;
	if (Array.isArray(payload?.result)) return payload.result;
	return [];
};

const createServiceState = (services = []) =>
	services.reduce((acc, service) => {
		if (service?.key) acc[service.key] = false;
		return acc;
	}, {});

const ManageSites = () => {
	const { fetchSites } = useSite();
	const { optionKeys } = useOptions();
	const { user, hasPermission } = useAuth();
	const actorRoleType = useMemo(
		() => String(user?.role?.role_type || user?.role?.role_name || "").trim().toLowerCase(),
		[user?.role?.role_type, user?.role?.role_name]
	);
	const [sites, setSites] = useState([]);
	const [allUsers, setAllUsers] = useState([]);
	const [error, setError] = useState("");
	const [formErrors, setFormErrors] = useState({});
	const [loading, setLoading] = useState(true);
	const [selectedSiteData, setSelectedSiteData] = useState(null);
	const [editFormLoading, setEditFormLoading] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);
	const [tabIndex, setTabIndex] = useState(0);
	const [siteUsers, setSiteUsers] = useState([]);
	const [usersLoading, setUsersLoading] = useState(false);
	const [userModalState, setUserModalState] = useState({
		open: false,
		site: null,
		users: [],
		loading: false,
	});
	// Addresses state
	const [addresses, setAddresses] = useState([]);
	const [addrModalOpen, setAddrModalOpen] = useState(false);
	const [addrForm, setAddrForm] = useState({
		id: null,
		address_name: "",
		address_type: "Shipping",
		street1: "",
		street2: "",
		street3: "",
		city: "",
		state: "",
		zip: "",
		country: "",
	});
	const [addrSaving, setAddrSaving] = useState(false);
	const [addrErrors, setAddrErrors] = useState({});
	const [addrAlert, setAddrAlert] = useState("");
	const { t } = useTranslation();
	const containerRef = useRef(null);
	const serviceOptions = React.useMemo(() => resolveServiceOptions(optionKeys), [optionKeys]);
	const {
		servicesEnabled,
		showLegalEntity,
		showSite,
		showWarehouse,
		showPaymentTerms,
		showOnHold,
	} = getCustomerLocationOptionFlags(optionKeys);
	const hasServiceDefinitions = servicesEnabled && serviceOptions.length > 0;
	const [formData, setFormData] = useState({
		default_legal_entity: "AUSA",
		site_currency: t('UI_TEXT.USD').toLowerCase(),
		site_name: "",
		warehouse: "",
		discount_code: "",
	});
	const [serviceMeta, setServiceMeta] = useState(() => createServiceState(serviceOptions));
	const [servicesLoading, setServicesLoading] = useState(false);
	const [servicesSaving, setServicesSaving] = useState(false);
	const [servicesError, setServicesError] = useState('');

	const [shippingSites, setShippingSites] = useState([]);
	const [shippingWarehouses, setShippingWarehouses] = useState([]);
	const [filteredSites, setFilteredSites] = useState([]);
	const [filteredWarehouses, setFilteredWarehouses] = useState([]);
	const [fallbackActorSiteIds, setFallbackActorSiteIds] = useState([]);
	const enableDiscountField = getBooleanOption(
		optionKeys?.enableDiscountField ?? optionKeys?.options?.enableDiscountField
	);
	const canManageLocationAddresses =
		optionKeys?.enableCheckoutAddressEditing == null
			? true
			: getBooleanOption(optionKeys?.enableCheckoutAddressEditing ?? optionKeys?.options?.enableCheckoutAddressEditing);
	const actorSiteIds = useMemo(() => {
		const fromAllocs = Array.isArray(user?.allocations)
			? user.allocations.map((a) => Number(a?.site_id)).filter((id) => Number.isFinite(id) && id > 0)
			: [];
		return Array.from(new Set(fromAllocs));
	}, [user?.allocations]);
	useEffect(() => {
		let mounted = true;
		const loadFallbackActorScope = async () => {
			if (Number(user?.user_id) === 1 || actorSiteIds.length > 0 || !user?.user_id) {
				if (mounted) setFallbackActorSiteIds([]);
				return;
			}
			try {
				const resp = await getUserAllocations(user.user_id);
				const allocations = Array.isArray(resp?.data?.allocations) ? resp.data.allocations : [];
				const ids = Array.from(
					new Set(
						allocations
							.map((a) => Number(a?.site_id))
							.filter((id) => Number.isFinite(id) && id > 0)
					)
				);
				if (mounted) setFallbackActorSiteIds(ids);
			} catch (_) {
				if (mounted) setFallbackActorSiteIds([]);
			}
		};
		loadFallbackActorScope();
		return () => {
			mounted = false;
		};
	}, [actorSiteIds, user?.user_id]);
	const effectiveActorSiteIds = useMemo(() => {
		if (actorSiteIds.length > 0) return actorSiteIds;
		return fallbackActorSiteIds;
	}, [actorSiteIds, fallbackActorSiteIds]);
	const actorHasAllSiteVisibility = useMemo(
		() =>
			Number(user?.user_id) === 1 ||
			((actorRoleType === "admin" || getBooleanOption(user?.role?.permissions?.user_management?.manage_all_sites)) &&
				effectiveActorSiteIds.length === 0),
		[user?.user_id, actorRoleType, user?.role?.permissions?.user_management?.manage_all_sites, effectiveActorSiteIds.length]
	);
	const scopedSites = useMemo(() => {
		if (actorHasAllSiteVisibility) return sites || [];
		const allowedSet = new Set((effectiveActorSiteIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0));
		return (sites || []).filter((site) => allowedSet.has(Number(site?.site_id)));
	}, [actorHasAllSiteVisibility, sites, effectiveActorSiteIds]);

	useEffect(() => {
		if (!hasServiceDefinitions && tabIndex > 1) {
			setTabIndex(0);
		}
	}, [hasServiceDefinitions, tabIndex]);
	useEffect(() => {
		if (!selectedSiteData?.site_id) return;
		const exists = (scopedSites || []).some((site) => Number(site?.site_id) === Number(selectedSiteData?.site_id));
		if (!exists) {
			setSelectedSiteData(null);
			setSiteUsers([]);
		}
	}, [scopedSites, selectedSiteData?.site_id]);

	const handleScroll = () => {
		containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	};

	const resolveUsersList = async () => {
		if (Array.isArray(allUsers) && allUsers.length > 0) {
			return allUsers;
		}
		const resp = await getAllUsers();
		const normalized = normalizeUsersResponse(resp).filter((user) => user?.user_id !== 1);
		setAllUsers(normalized);
		return normalized;
	};

	const openAddrModal = (row) => {
		setAddrErrors({});
		setAddrAlert("");
		if (!selectedSiteData?.site_id) {
			Swal.fire({ icon: 'info', title: t('GENERAL.SELECT_A_SITE_FIRST') });
			return;
		}
		if (row) {
			setAddrForm({
				id: row.id,
				address_name: row.address_name || "",
				address_type: row.address_type || "Shipping",
				street1: row.street1 || "",
				street2: row.street2 || "",
				street3: row.street3 || "",
				city: row.city || "",
				state: row.state || "",
				zip: row.zip || "",
				country: row.country || "",
			});
		} else {
			setAddrForm({ id: null, address_name: "", address_type: "Shipping", street1: "", street2: "", street3: "", city: "", state: "", zip: "", country: "" });
		}
		setAddrModalOpen(true);
	};

	const handleOpenUserModal = async (siteRow) => {
		if (!siteRow?.site_id) return;
		setUserModalState({
			open: true,
			site: siteRow,
			users: [],
			loading: true,
		});
		try {
			const usersList = await resolveUsersList();
			const filtered = filterUsersForSite(usersList, siteRow.site_id);
			setUserModalState({
				open: true,
				site: siteRow,
				users: filtered,
				loading: false,
			});
		} catch (error) {
			setUserModalState({
				open: true,
				site: siteRow,
				users: [],
				loading: false,
			});
		}
	};

	const handleCloseUserModal = () => {
		setUserModalState({
			open: false,
			site: null,
			users: [],
			loading: false,
		});
	};

	const saveAddress = async () => {
		if (!selectedSiteData?.site_id) return;
		setAddrAlert("");
		setAddrErrors({});
		const payload = { ...addrForm, site_id: selectedSiteData.site_id };
		const fieldErrors = {};
		if (!`${payload.address_name || ""}`.trim()) {
			fieldErrors.address_name = t('GENERAL.ADDRESS_NAME_IS_REQUIRED');
		}
		const requiredAddressFields = [
			{ key: 'street1', label: 'Street' },
			{ key: 'city', label: t('LABELS.CITY') },
			{ key: 'state', label: 'State' },
			{ key: 'zip', label: 'Zip' },
			{ key: 'country', label: 'Country' },
		];
		requiredAddressFields.forEach(({ key, label }) => {
			if (!`${payload[key] || ""}`.trim()) {
				fieldErrors[key] = `${label} is required`;
			}
		});
		if (Object.keys(fieldErrors).length > 0) {
			setAddrErrors(fieldErrors);
			setAddrAlert(t('GENERAL.PLEASE_FILL_IN_THE_HIGHLIGHTED_FIELDS'));
			return;
		}
		setAddrSaving(true);
		try {
			if (payload.id) {
				await updateSiteAddress(payload.id, payload);
			} else {
				await createSiteAddress(payload);
			}
			const resp = await getSiteAddresses(selectedSiteData.site_id);
			setAddresses(resp?.data || []);
			setAddrModalOpen(false);
			toast.trigger({ message: t('SITE_MANAGEMENT.ADDRESS_SAVED_SUCCESSFULLY'), severity: 'success' });
		} catch (e) {
			setAddrAlert(t('GENERAL.FAILED_TO_SAVE_ADDRESS_PLEASE_TRY_AGAIN'));
		} finally {
			setAddrSaving(false);
		}
	};

	const deleteAddress = async (row) => {
		const confirm = await Swal.fire({ icon: 'warning', title: 'Delete address?', text: row.address_name, showCancelButton: true });
		if (!confirm.isConfirmed) return;
		try {
			await deleteSiteAddress(row.id);
			const resp = await getSiteAddresses(selectedSiteData.site_id);
			setAddresses(resp?.data || []);
			toast.trigger({ message: t('SITE_MANAGEMENT.ADDRESS_DELETED_SUCCESSFULLY'), severity: 'success' });
		} catch {
			toast.trigger({ message: t('GENERAL.FAILED_TO_DELETE_ADDRESS'), severity: 'error' });
		}
	};

	// Handle Legal Entity → filter Sites & Warehouses (with AUSA fallback)
	useEffect(() => {
		if (formData.default_legal_entity) {
			let filteredS = shippingSites.filter(
				(site) => site.data_area_id === formData.default_legal_entity
			);
			let filteredW = shippingWarehouses.filter(
				(wh) => wh.data_area_id === formData.default_legal_entity
			);

			// 🟡 Fallback: if no sites or warehouses found for selected entity, use AUSA
			if (filteredS.length === 0 && filteredW.length === 0) {
				filteredS = shippingSites.filter((s) => s.data_area_id === "AUSA");
				filteredW = shippingWarehouses.filter((w) => w.data_area_id === "AUSA");
			}

			setFilteredSites(filteredS);
			setFilteredWarehouses(filteredW);
		}
	}, [formData.default_legal_entity, shippingSites, shippingWarehouses]);

	// Handle Site → auto-select Legal Entity & filter Warehouses (with fallback)
	useEffect(() => {
		if (formData.site_name) {
			const selectedSite = shippingSites.find(
				(site) => site.shipping_site_id === formData.site_name
			);

			if (selectedSite) {
				const entity = selectedSite.data_area_id;
				setFormData((prev) => ({
					...prev,
					default_legal_entity: entity,
				}));

				let filteredW = shippingWarehouses.filter(
					(wh) => wh.data_area_id === entity
				);

				// 🟡 Fallback: if site’s entity has no warehouses, use AUSA
				if (filteredW.length === 0) {
					filteredW = shippingWarehouses.filter((wh) => wh.data_area_id === "AUSA");
				}

				setFilteredWarehouses(filteredW);
			}
		}
	}, [formData.site_name, shippingSites, shippingWarehouses]);

	// Handle Warehouse → auto-select Legal Entity & filter Sites (with fallback)
	useEffect(() => {
		if (formData.warehouse) {
			const selectedWh = shippingWarehouses.find(
				(wh) => wh.shipping_warehouse_id === formData.warehouse
			);

			if (selectedWh) {
				const entity = selectedWh.data_area_id;
				setFormData((prev) => ({
					...prev,
					default_legal_entity: entity,
				}));

				let filteredS = shippingSites.filter(
					(s) => s.data_area_id === entity
				);

				// 🟡 Fallback: if warehouse’s entity has no sites, use AUSA
				if (filteredS.length === 0) {
					filteredS = shippingSites.filter((s) => s.data_area_id === "AUSA");
				}

				setFilteredSites(filteredS);
			}
		}
	}, [formData.warehouse, shippingWarehouses, shippingSites]);



	useEffect(() => {
		const fetchShippingData = async () => {
			const shippingSiteReqBody = { query: `SELECT * FROM shipping_sites` };
			const shippingWarehouseReqBody = { query: `SELECT * FROM shipping_warehouses` };

			const [shippingSitesResp, shippingWarehousesResp] = await Promise.all([
				getCustomSitesData("SELECT * FROM shipping_sites"),
				getCustomSitesData("SELECT * FROM shipping_warehouses"),
			]);

			setShippingSites(shippingSitesResp?.data || []);
			setShippingWarehouses(shippingWarehousesResp?.data || []);
		};
		fetchShippingData();
	}, []);

	// Fetch addresses when a site is selected
	useEffect(() => {
		const load = async () => {
			if (!selectedSiteData?.site_id) { setAddresses([]); return; }
			try {
				const resp = await getSiteAddresses(selectedSiteData.site_id);
				setAddresses(resp?.data?.data ?? resp?.data ?? []);
			} catch { setAddresses([]); }
		};
		load();
	}, [selectedSiteData]);

	useEffect(() => {
		setServiceMeta(createServiceState(serviceOptions));
	}, [serviceOptions]);

	useEffect(() => {
		if (!hasServiceDefinitions) return;
		if (!selectedSiteData?.site_id) {
			setServiceMeta(createServiceState(serviceOptions));
			setServicesError('');
			setServicesLoading(false);
			return;
		}
		let active = true;
		const siteId = Number(selectedSiteData.site_id);
		if (Number.isNaN(siteId)) {
			setServiceMeta(createServiceState());
			return;
		}
		const keysClause = serviceOptions
			.filter((service) => service?.key)
			.map((service) => `'${service.key.replace(/'/g, "''")}'`)
			.join(", ");
		if (!keysClause) {
			setServiceMeta(createServiceState(serviceOptions));
			return;
		}
		setServicesLoading(true);
		setServicesError('');
		const query = `SELECT meta_key, meta_value FROM site_meta WHERE site_id=${siteId} AND meta_key IN (${keysClause})`;
		getCustomSitesData(query)
			.then((resp) => {
				if (!active) return;
				const rows = normalizeCustomQueryResponse(resp);
				const nextState = createServiceState(serviceOptions);
				rows.forEach((row) => {
					const key = row?.meta_key;
					if (key && Object.prototype.hasOwnProperty.call(nextState, key)) {
						nextState[key] = getBooleanOption(row?.meta_value);
					}
				});
				setServiceMeta(nextState);
			})
			.catch(() => {
				if (active) setServiceMeta(createServiceState(serviceOptions));
			})
			.finally(() => {
				if (active) setServicesLoading(false);
			});
		return () => {
			active = false;
		};
	}, [hasServiceDefinitions, selectedSiteData, serviceOptions]);


	useEffect(() => {
		const loadSites = async () => {
			try {

				let query = `SELECT 
                    s.*, 
                    c.customer_number AS parent_customer_number, 
                    c.customer_name AS parent_customer_name,
                    sm.meta_value as site_legal_entity, 
                    sm1.meta_value as site_currency, 
                    sm2.meta_value as site_name, 
                    sm3.meta_value as warehouse 
                FROM sites as s
                LEFT JOIN customers c ON s.cus_id = c.cus_id
                LEFT JOIN site_meta as sm ON s.site_id=sm.site_id AND sm.meta_key='site_legal_entity'
                LEFT JOIN site_meta as sm1 ON s.site_id=sm1.site_id AND sm1.meta_key='site_currency'
                LEFT JOIN site_meta as sm2 ON s.site_id=sm2.site_id AND sm2.meta_key='site_name'
                LEFT JOIN site_meta as sm3 ON s.site_id=sm3.site_id AND sm3.meta_key='warehouse'`


				const shippingSiteReqBody = {
					query: `SELECT * FROM shipping_sites`
				}

				const shippingWarehouseReqBody = {
					query: `SELECT * FROM shipping_warehouses`
				}

				const [sitesResp, usersResp] = await Promise.all([
					getCustomSitesData(query),
					getAllUsers(),
				]);

				const sitesData = sitesResp?.data || [];
				const normalizedUsers = normalizeUsersResponse(usersResp).filter(
					(user) => user?.user_id !== 1
				);
				setAllUsers(normalizedUsers);

				const sitesWithUserCount = withUserCounts(sitesData, normalizedUsers);

				setSites(sitesWithUserCount);
			} catch (err) {
				setError("Failed to fetch sites: " + err.message);
			} finally {
				setLoading(false);
			}
		};
		loadSites();
	}, []);

	useEffect(() => {
		// Simulate an async operation, e.g., fetching data or processing
		const loadForm = () => {
			setTimeout(() => {
				setEditFormLoading(false); // Set loading to false once data is loaded
			}, 2000); // Simulating loading delay of 2 seconds
		};
		let active = true;

		if (selectedSiteData) {
			setEditFormLoading(true);
			setFormData({
				default_legal_entity: selectedSiteData.site_legal_entity ?? "AUSA",
				site_currency: selectedSiteData.site_currency ?? t('UI_TEXT.USD').toLowerCase(),
				site_name: selectedSiteData.site_name ?? "",
				warehouse: selectedSiteData.warehouse ?? "",
				discount_code: selectedSiteData.discount_code ?? "",
			});
			// Reset to the first visible tab (Users) and load users for this site
			setTabIndex(0);
			setUsersLoading(true);
			resolveUsersList()
				.then((usersList) => {
					if (!active) return;
					const filtered = filterUsersForSite(usersList, selectedSiteData.site_id);
					setSiteUsers(filtered);
				})
				.catch(() => {
					if (active) setSiteUsers([]);
				})
				.finally(() => {
					if (active) setUsersLoading(false);
				});
			loadForm();
		}

		return () => {
			active = false;
		};
	}, [selectedSiteData, allUsers]);

	const handleChange = (event) => {
		const { name, value } = event.target;
		setFormData((prevData) => ({
			...prevData,
			[name]: value,
		}));
	};

	const resolveDefaultLegalEntity = () =>
		formData.default_legal_entity || selectedSiteData?.site_legal_entity || "AUSA";

	const resolveDefaultSiteName = () =>
		formData.site_name ||
		selectedSiteData?.site_name ||
		filteredSites[0]?.shipping_site_id ||
		shippingSites[0]?.shipping_site_id ||
		"";

	const resolveDefaultWarehouse = () =>
		formData.warehouse ||
		selectedSiteData?.warehouse ||
		filteredWarehouses[0]?.shipping_warehouse_id ||
		shippingWarehouses[0]?.shipping_warehouse_id ||
		"";

	const buildSiteMetaPayload = (extras = {}) => {
		if (!selectedSiteData?.site_id) return null;
		const resolvedLegalEntity = resolveDefaultLegalEntity();
		const resolvedSiteName = resolveDefaultSiteName();
		const resolvedWarehouse = resolveDefaultWarehouse();
		const payload = {
			site_id: selectedSiteData.site_id,
			cus_id: selectedSiteData.cus_id,
			site_name: resolvedSiteName,
			warehouse: resolvedWarehouse,
			site_legal_entity: resolvedLegalEntity,
			site_currency: formData.site_currency || selectedSiteData.site_currency || t('UI_TEXT.USD'),
		};
		if (enableDiscountField) {
			payload.discount_code = formData.discount_code ?? selectedSiteData.discount_code ?? "";
		}
		return { ...payload, ...extras };
	};

	const handleSubmit = async (event) => {
		event.preventDefault();

		// Validate the form fields
		const newErrors = {};
		if (showSite && !formData.site_name) newErrors.site_name = t('ALERT_MESSAGES.SITE_NAME_REQUIRED');
		if (showWarehouse && !formData.warehouse) newErrors.warehouse = t('ALERT_MESSAGES.WAREHOUSE_REQUIRED');
		if (showLegalEntity && !formData.default_legal_entity) newErrors.default_legal_entity = t('ALERT_MESSAGES.LEGAL_ENTITY_REQUIRED');
		if (!formData.site_currency) newErrors.site_currency = t('ALERT_MESSAGES.CURRENCY_REQUIRED');

		// Validate discount code if enabled and provided
		if (enableDiscountField && formData.discount_code !== "") {
			const discountValue = parseFloat(formData.discount_code);
			if (isNaN(discountValue)) {
				newErrors.discount_code = t('ERRORS.DISCOUNT_CODE_CANNOT_BE_EMPTY');
			}
		}

		if (Object.keys(newErrors).length > 0) {
			setFormErrors(newErrors);
			return;
		}

		// Clear errors if no issues
		setFormErrors({});

		const req_body = buildSiteMetaPayload();
		if (!req_body) return;
		setIsSubmitting(true);
		setIsSubmitSuccess(false);

		try {
			// Make the POST request
			const response = await updateCustomerSites(req_body);
			// Handle success response
			if (response.status) {
				try {
					const requestBody = {
						query: `SELECT 
							s.*, 
							c.customer_number AS parent_customer_number, 
							c.customer_name AS parent_customer_name,
							sm.meta_value as site_legal_entity, 
							sm1.meta_value as site_currency, 
							sm2.meta_value as site_name, 
							sm3.meta_value as warehouse 
						FROM sites as s
						LEFT JOIN customers c ON s.cus_id = c.cus_id
						LEFT JOIN site_meta as sm ON s.site_id=sm.site_id AND sm.meta_key='site_legal_entity'
						LEFT JOIN site_meta as sm1 ON s.site_id=sm1.site_id AND sm1.meta_key='site_currency'
						LEFT JOIN site_meta as sm2 ON s.site_id=sm2.site_id AND sm2.meta_key='site_name'
						LEFT JOIN site_meta as sm3 ON s.site_id=sm3.site_id AND sm3.meta_key='warehouse'`
					};
					const refreshed = await getCustomSitesData(requestBody.query);
					const refreshedSites = refreshed?.data?.data ?? refreshed?.data ?? [];
					setSites(withUserCounts(refreshedSites, allUsers));
				} catch (err) {
					// ignore
				}

				setIsSubmitting(false);
				setIsSubmitSuccess(true);
				await fetchSites();
				// Show success alert
				Swal.fire({
					icon: "success",
					title: t('ALERT_MESSAGES.SITE_UPDATED_SUCCESSFULLY'),
					text: t('ALERT_MESSAGES.THE_SITE_META_UPDATED'),
				});
				fetchSites();
				setSelectedSiteData(null);

				setTimeout(() => {
					setIsSubmitting(false);
					setIsSubmitSuccess(false);
				}, 2000);
			} else {
				// Show error alert
				setIsSubmitting(false);
				setIsSubmitSuccess(false);
				Swal.fire({
					icon: "error",
					title: t('ALERT_MESSAGES.ERROR'),
					text: response.message || t('ALERT_MESSAGES.AN_ERROR_OCCURED_UPDATING_SITE'),
				});
			}
		} catch (error) {
			// Show error alert on request failure
			setIsSubmitting(false);
			setIsSubmitSuccess(false);
			Swal.fire({
				icon: "error",
				title: t('ALERT_MESSAGES.ERROR'),
				text: t('ALERT_MESSAGES.AN_EXPECTED_ERROR'),
			});
		}
	};

	const handleServiceToggle = (serviceKey) => {
		setServiceMeta((prev) => {
			if (!Object.prototype.hasOwnProperty.call(prev, serviceKey)) return prev;
			return { ...prev, [serviceKey]: !prev[serviceKey] };
		});
	};

	const handleSaveServices = async () => {
		if (!hasServiceDefinitions || !selectedSiteData?.site_id) return;
		const servicesPayload = serviceOptions.reduce((acc, service) => {
			if (!service?.key) return acc;
			acc[service.key] = serviceMeta[service.key] ? "1" : "0";
			return acc;
		}, {});
		const req_body = buildSiteMetaPayload({ services_meta: servicesPayload });
		if (!req_body) return;
		setServicesSaving(true);
		setServicesError('');
		try {
			const response = await updateCustomerSites(req_body);
			if (response.status) {
				toast.trigger({ message: t('ALERT_MESSAGES.SITE_UPDATED_SUCCESSFULLY'), severity: 'success' });
				await fetchSites();
			} else {
				throw new Error(response?.message || t('ALERT_MESSAGES.AN_ERROR_OCCURED_UPDATING_SITE'));
			}
		} catch (error) {
			const message = error?.message || t('ALERT_MESSAGES.AN_ERROR_OCCURED_UPDATING_SITE');
			setServicesError(message);
			toast.trigger({ message, severity: 'error' });
		} finally {
			setServicesSaving(false);
		}
	};

	const handleEditSite = async (row) => { setSelectedSiteData(row); };

	const handleTabChange = (event, newValue) => {
		setTabIndex(newValue);
	};

	if (loading) {
		return (
			<DatagridLoading
				message={""}
				actionButtons={2}
				columns={5}
				rows={8}
			/>
		);
	}

	if (error) {
		return (
			<Alert severity="error" className="error-alert">
				{error}
			</Alert>
		);
	}

	const addressColumns = [
		{ field: 'address_name', headerName: t('LABELS.NAME'), flex: 1.2 },
		{ field: 'address_type', headerName: t('LABELS.TYPE'), flex: 0.8 },
		{ field: 'street1', headerName: t('GENERAL.STREET_1'), flex: 1 },
		{ field: 'street2', headerName: t('GENERAL.STREET_2'), flex: 1 },
		{ field: 'street3', headerName: t('GENERAL.STREET_3'), flex: 1 },
		{ field: 'city', headerName: t('LABELS.CITY'), flex: 0.8 },
		{ field: 'state', headerName: t('LABELS.STATE'), flex: 0.8 },
		{ field: 'zip', headerName: t('LABELS.ZIP'), flex: 0.6 },
		{ field: 'country', headerName: t('LABELS.COUNTRY'), flex: 0.8 },
		...(canManageLocationAddresses ? [{
			field: 'actions', headerName: t('TABLE_HEADERS.ACTIONS'), flex: 0.8,
			sortable: false, filterable: false,
			headerAlign: "center",
			align: "center",
			renderCell: (params) => (
				<Box sx={{ display: "flex", flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>

					<Box sx={{ display: "flex", justifyContent: 'center', alignItems: 'center' }}>

						{params.row.is_default ? (
							<Typography variant="caption" color="text.secondary">Default</Typography>
						) : (
							hasPermission('site_management.edit_sites') && (
								<>
									<Tooltip title={t("UI_TEXT.TOOLTIP_EDIT")}>
										<IconButton color="primary" onClick={() => openAddrModal(params.row)}>
											<EditIcon />
										</IconButton>
									</Tooltip>
									<Tooltip title={t("UI_TEXT.TOOLTIP_DELETE")}>
										<IconButton color="error" onClick={() => deleteAddress(params.row)}>
											<DeleteIcon />
										</IconButton>
									</Tooltip>
								</>
							)
						)}
					</Box>
				</Box>)
		}] : [])
	]

	const userColumns = [
		{ field: 'user_display_name', headerName: t('TABLE_HEADERS.NAME'), flex: 1.5 },
		{ field: 'user_email', headerName: t('TABLE_HEADERS.EMAIL'), flex: 2 },
		{ field: 'user_role_name', headerName: t('LABELS.ROLE'), flex: 1 },
		{
			field: 'user_status',
			headerName: t('TABLE_HEADERS.STATUS'),
			flex: 0.8,
			valueFormatter: (value) => (value === 0 ? 'Pending' : 'Approved'),
		},
	];

	// Grid columns matching requirements
	const columns = [
		{ field: "customer_site_name", headerName: t('LABELS.SITE_NAME'), flex: 1.5 },
		{ field: "customer_site_number", headerName: t('TABLE_HEADERS.SITE_NUMBER'), flex: 1 },
		{ field: "parent_customer_number", headerName: t('TABLE_HEADERS.CUSTOMER_NUMBER'), flex: 1 },
		{ field: "address_full_ln", headerName: t('TABLE_HEADERS.SITE_ADDRESS'), flex: 2 },
		{
			field: "user_count",
			headerName: t('GENERAL.USER_1'),
			width: 200,
			align: "center",
			headerAlign: "center",
			renderCell: (params) => (
				<Button
					variant="text"
					size="small"
					onClick={(event) => {
						event.stopPropagation();
						handleOpenUserModal(params.row);
					}}
				>
					{params.value ?? 0}
				</Button>
			),
		},
		{
			headerName: t('TABLE_HEADERS.ACTIONS'),
			sortable: false,
			width: 100,
			headerAlign: "center",
			align: "center",
			renderCell: (params) => (
				<Box sx={{ display: "flex", flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>

					<Box sx={{ display: "flex", justifyContent: 'center', alignItems: 'center' }}>
						{hasPermission('site_management.edit_sites') && (
							<Tooltip title={t('GENERAL.EDIT_SITE', { defaultValue: 'Edit Site' })}>
								<IconButton color="primary" aria-label="Edit Site" onClick={(e) => { e.stopPropagation(); handleEditSite(params.row); handleScroll() }}>
									<EditIcon />
								</IconButton>
							</Tooltip>
						)}
					</Box>
				</Box>
			),
		},
	];


	return (
		<div>
				<Datagrid
					loading={loading}
					data={scopedSites}
					columns={columns}
				checkboxSelection={false}
				rowIdField="site_id"
				sortBy={[{ field: "customer_site_name", sort: "asc" }]}
				hideFilterFromColumnMenu
				lockActiveColumnInManageColumns
				preventHidingLastColumn
			/>
			<Box sx={{ width: '100%' }} ref={containerRef}>


				{selectedSiteData && (
					<>
						<Box sx={{ width: "100%", display: 'flex', justifyContent: 'space-between', alignItems: 'center', columnGap: 2, m1: 1 }}>
							<Box >
								<Typography variant="h5" sx={{ fontWeight: 'bold' }}>
									{t('SITE_MANAGEMENT.EDITING_LOCATION')}: {selectedSiteData?.customer_site_name || ''}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{t('TABLE_HEADERS.SITE_NUMBER')}: {selectedSiteData?.customer_site_number || ''}
								</Typography>
							</Box>
							<Button variant="outlined" color="primary" onClick={() => setSelectedSiteData(null)}>{t('BUTTON.CANCEL')}
							</Button>
						</Box>
						<div
							style={{
								marginTop: '15px',
								display: "flex",
								flexDirection: "row",
								columnGap: "20px",
								width: "100%",
							}}>
							<Box
								flex="1"
								minWidth="300px"
								padding="20px"
								sx={{
									backgroundColor: "#ffffff",
									borderRadius: "8px",
									boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
								}}>
								<Typography
									variant="h6"
									sx={{
										color: "var(--primary-main)",
										fontWeight: "bold",
										marginBottom: "10px",
									}}>
									{t('UI_TEXT.CUSTOMER_INFORMATION')}
								</Typography>
								<Typography variant="body1" color="text.secondary">
									<strong>{t('UI_TEXT.CUSTOMER_NAME')}</strong> {selectedSiteData.customer_site_name}
								</Typography>
								<Typography variant="body1" color="text.secondary">
									<strong>{t('UI_TEXT.CUSTOMER')}</strong> {selectedSiteData.customer_site_number}
								</Typography>
							</Box>
							<Box
								flex="1"
								minWidth="300px"
								padding="20px"
								sx={{
									backgroundColor: "#ffffff",
									borderRadius: "8px",
									boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
								}}>
								<Typography
									variant="h6"
									sx={{
										color: "var(--primary-main)",
										fontWeight: "bold",
										marginBottom: "10px",
									}}>
									{t('UI_TEXT.ADDRESS_INFORMATION')}
								</Typography>
								<Typography variant="body1" color="text.secondary">
									<strong>{t('UI_TEXT.ADDRESS_NAME')}</strong> {selectedSiteData.address_name_ln}
								</Typography>
								<Typography variant="body1" color="text.secondary">
									<strong>{t('UI_TEXT.FULL_ADDRESS')}</strong> {selectedSiteData.address_full_ln}
								</Typography>
							</Box>

							<Box
								flex="1"
								minWidth="300px"
								padding="20px"
								sx={{
									backgroundColor: "#ffffff",
									borderRadius: "8px",
									boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
								}}>
								<Typography
									variant="h6"
									sx={{
										color: "var(--primary-main)",
										fontWeight: "bold",
										marginBottom: "10px",
									}}>
									{t('UI_TEXT.CCR_INFORMATION')}
								</Typography>

								<Typography variant="body1" color="text.secondary">
									<strong>{t('UI_TEXT.CCR_NAME')}</strong> {selectedSiteData.ccr_name}
								</Typography>
								<Typography variant="body1" color="text.secondary">
									<strong>{t('UI_TEXT.CCR_EMAIL')}</strong> {selectedSiteData.ccr_email}
								</Typography>
								<Typography variant="body1" color="text.secondary">
									<strong>{t('UI_TEXT.CCR_PHONE')}</strong> {selectedSiteData.ccr_phone}
								</Typography>
							</Box>
						</div>

						{/* Edit context title/subtitle */}


						{/* Tabs for Location Options and Users */}
						<Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
							<Tabs value={tabIndex} onChange={handleTabChange} aria-label="site edit tabs">
								{/* <Tab label={t('SITE_MANAGEMENT.LOCATION_OPTIONS')} /> */}
								<Tab label={t('LABELS.USERS', { count: siteUsers?.length || 0 })} />
								<Tab label={t('BUTTON.ADDRESSES', { count: addresses?.length || 0 })} />
								{hasServiceDefinitions && <Tab label={t('LABELS.SERVICES')} />}
							</Tabs>
						</Box>

						{/* Tab Panel: Location Options (existing form) */}
						{false && (
							<form onSubmit={handleSubmit}>
								<Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 3 }}>
									<Box sx={{ display: "flex", gap: 2 }}>
										{showLegalEntity && (
											<FormControl fullWidth>
												<InputLabel id="demo-simple-select-label">{t('LABELS.DEFAULT_LEGAL_ENTITIES')}</InputLabel>
												<Select
													labelId="demo-simple-select-label"
													id="demo-simple-select"
													value={formData.default_legal_entity ?? "AUSA"}
													label={t('LABELS.DEFAULT_LEGAL_ENTITIES')}
													name="default_legal_entity"
													onChange={handleChange}
													size="small"
													error={!!formErrors.default_legal_entity}
													helperText={formErrors.default_legal_entity}>
													<MenuItem value="AUSA">AUSA</MenuItem>
													<MenuItem value="ACAN">ACAN</MenuItem>
													<MenuItem value="EUUK">EUUK</MenuItem>
													<MenuItem value="SING">SING</MenuItem>
												</Select>
											</FormControl>
										)}

										<FormControl fullWidth>
											<InputLabel id="demo-simple-select-label1">{t('LABELS.SITE_CURRENCY')}</InputLabel>
											<Select
												labelId="demo-simple-select-label1"
												id="demo-simple-select1"
												value={formData.site_currency ?? t('UI_TEXT.USD').toLowerCase()}
												label={t('LABELS.SITE_CURRENCY')}
												onChange={handleChange}
												name="site_currency"
												size="small"
												error={!!formErrors.site_currency}
												helperText={formErrors.site_currency}>
												<MenuItem value="usd">{t('UI_TEXT.USD')}</MenuItem>
												<MenuItem value="cad">{t('UI_TEXT.CAD')}</MenuItem>
												<MenuItem value="gbp">{t('UI_TEXT.GBP')}</MenuItem>
												<MenuItem value="eur">{t('UI_TEXT.EUR')}</MenuItem>
												<MenuItem value="sgd">{t('UI_TEXT.SGD')}</MenuItem>
												<MenuItem value="inr">{t('UI_TEXT.INR')}</MenuItem>
											</Select>
										</FormControl>
									</Box>

									{(showSite || showWarehouse) && (
										<Box sx={{ display: "flex", gap: 2 }}>
											{showSite && (
												<Autocomplete
													fullWidth
													options={filteredSites.map((site) => ({
														id: site.shipping_site_id,
														name: site.shipping_site_name,
														data_area_id: site.data_area_id,
													}))}

													groupBy={(option) => option.data_area_id}

													// Text shown in the textbox
													getOptionLabel={(option) => option?.id || ""}

													// Important: prevent mismatch when option is undefined
													isOptionEqualToValue={(option, value) => option.id === value.id}

													// Value must be an object that matches from options
													value={
														filteredSites
															.map((s) => ({
																id: s.shipping_site_id,
																name: s.shipping_site_name,
																data_area_id: s.data_area_id,
															}))
															.find((s) => s.id === formData.site_name) || null
													}

													// When selecting, store only ID in formData
													onChange={(event, newValue) => {
														setFormData((prev) => ({
															...prev,
															site_name: newValue ? newValue.id : "",
														}));
													}}

													renderInput={(params) => (
														<TextField
															{...params}
															label={t('SITE_MANAGEMENT.SITE_1')}
															size="small"
															fullWidth
															error={!!formErrors.site_name}
															helperText={formErrors.site_name}
														/>
													)}

													renderGroup={(params) => (
														<li key={params.key}>
															<GroupHeader>{params.group}</GroupHeader>
															<GroupItems>{params.children}</GroupItems>
														</li>
													)}

													renderOption={(props, option) => (
														<li {...props} style={{ width: "100%" }}>
															<Box
																sx={{
																	display: "flex",
																	flexDirection: "column",
																	alignItems: "flex-start",
																	width: "100%",
																	px: 0.5,
																	py: 0.5,
																}}
															>
																<Typography variant="body1" fontSize={"13px"} fontWeight={"bold"}>
																	{option.id}
																</Typography>
																<Typography variant="body1" fontSize={"12px"} color="text.secondary">
																	{option.name}
																</Typography>
															</Box>
														</li>
													)}
												/>
											)}




											{showWarehouse && (
												<Autocomplete
													fullWidth
													options={filteredWarehouses.map((wh) => ({
														id: wh.shipping_warehouse_id,
														name: wh.shipping_warehouse_name,
														data_area_id: wh.data_area_id,
													}))}

													groupBy={(option) => option.data_area_id}

													getOptionLabel={(option) => option?.id || ""}
													isOptionEqualToValue={(option, value) => option.id === value.id}

													// Convert saved ID → matching option object
													value={
														filteredWarehouses
															.map((w) => ({
																id: w.shipping_warehouse_id,
																name: w.shipping_warehouse_name,
																data_area_id: w.data_area_id,
															}))
															.find((w) => w.id === formData.warehouse) || null
													}

													onChange={(event, newValue) => {
														setFormData((prev) => ({
															...prev,
															warehouse: newValue ? newValue.id : "",
														}));
													}}

													renderInput={(params) => (
														<TextField
															{...params}
															label={t('LABELS.WAREHOUSE')}
															size="small"
															fullWidth
															error={!!formErrors.warehouse}
															helperText={formErrors.warehouse}
														/>
													)}

													renderGroup={(params) => (
														<li key={params.key}>
															<GroupHeader>{params.group}</GroupHeader>
															<GroupItems>{params.children}</GroupItems>
														</li>
													)}

													renderOption={(props, option) => (
														<li {...props} style={{ width: "100%" }}>
															<Box
																sx={{
																	display: "flex",
																	flexDirection: "column",
																	alignItems: "flex-start",
																	width: "100%",
																	px: 0.5,
																	py: 0.5,
																}}
															>
																<Typography variant="body1" fontSize={"13px"} fontWeight={"bold"}>
																	{option.id}
																</Typography>
																<Typography variant="body1" fontSize={"12px"} color="text.secondary">
																	{option.name}
																</Typography>
															</Box>
														</li>
													)}
												/>
											)}
										</Box>
									)}

									{/* Disabled fields: On Hold Status & Payment Terms */}
									{(showOnHold || showPaymentTerms) && (
										<Box sx={{ display: "flex", gap: 2 }}>
											{showOnHold && (
												<TextField
													label={t('GENERAL.ON_HOLD_STATUS')}
													fullWidth
													name="on_hold_status"
													value={selectedSiteData?.site_status ?? ""}
													size="small"
													disabled
												/>
											)}

											{showPaymentTerms && (
												<TextField
													label={t('GENERAL.PAYMENT_TERMS')}
													fullWidth
													name="payment_terms"
													value={selectedSiteData?.payment_terms ?? ""}
													size="small"
													disabled
												/>
											)}
										</Box>
									)}

									<Box sx={{ display: "flex", gap: 2 }}>
										<TextField
											label={t('GENERAL.DISCOUNT_MARKUP_PERCENT')}
											fullWidth
											name="discount_code"
											type="number"
											inputProps={{ step: 0.01 }}
											value={formData.discount_code ?? ""}
											onChange={handleChange}
											size="small"
											disabled={!enableDiscountField}
											error={!!formErrors.discount_code}
											helperText={formErrors.discount_code || t('GENERAL.ENTER_DISCOUNT_MARKUP_PERCENTAGE')}
										/>
									</Box>

									<Box sx={{ display: "flex", justifyContent: "center", columnGap: 1.5 }}>
										<Button variant="outlined" color="primary" onClick={() => setSelectedSiteData(null)}>{t('BUTTON.CANCEL')}
										</Button>
										<Button variant="contained" color="primary" type="submit">
											{isSubmitting ? (
												<>
													<CircularProgress size={16} sx={{ color: "white", marginRight: 1 }} />
													{t('BUTTON.SUBMITTING')}
												</>
											) : isSubmitSuccess ? (
												<>
													<CheckCircleIcon sx={{ color: "white", marginRight: 1 }} />
													{t('BUTTON.SUBMITTED')}
												</>
											) : (
												t('BUTTON.SUBMIT')
											)}
										</Button>
									</Box>
								</Box>
							</form>
						)}

						{/* Tab Panel: Users */}
						{tabIndex === 0 && (
							<Box sx={{ mt: 2 }}>
								{usersLoading ? (
									<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 120 }}>
										<CircularProgress />
									</Box>
								) : (
									<Datagrid
										data={siteUsers}
										columns={userColumns}
										checkboxSelection={false}
										rowIdField="user_id"
										showSearchBar={true}
											hideFilterFromColumnMenu
											lockActiveColumnInManageColumns
											preventHidingLastColumn
									/>
								)}
							</Box>
						)}

						{/* Tab Panel: Addresses */}
						{tabIndex === 1 && (
							<Box sx={{ mt: 2 }}>
								<Datagrid
									data={addresses}
									columns={addressColumns}
									rowIdField="id"
									customButtons={hasPermission('site_management.edit_sites') && canManageLocationAddresses ? [{
										label: t('LABELS.NEW_ADDRESS'),
										icon: <AddIcon />,
										onClick: () => openAddrModal(),
									}] : []}
									hideFilterFromColumnMenu
									lockActiveColumnInManageColumns
									preventHidingLastColumn
								/>

								{canManageLocationAddresses && (
									<ModalWrapper
										open={addrModalOpen}
										setOpen={setAddrModalOpen}
										title={addrForm.id ? 'Edit Address' : t('LABELS.NEW_ADDRESS')}
										content={(
											<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1.5 }}>
												{addrAlert && (
													<Alert severity="error">{addrAlert}</Alert>
												)}
												<TextField
													label={t('UI_TEXT.ADDRESS_NAME')}
													size="small"
													value={addrForm.address_name}
													error={Boolean(addrErrors.address_name)}
													helperText={addrErrors.address_name || ""}
													onChange={e => {
														setAddrForm({ ...addrForm, address_name: e.target.value });
														setAddrErrors(prev => ({ ...prev, address_name: "" }));
														if (addrAlert) setAddrAlert("");
													}}
												/>
												<FormControl size="small">
													<InputLabel> {t('LABELS.TYPE')}</InputLabel>
													<Select label={t('LABELS.TYPE')} value={addrForm.address_type} onChange={e => setAddrForm({ ...addrForm, address_type: e.target.value })}>
														<MenuItem value="Shipping">Shipping</MenuItem>
														<MenuItem value="Billing">Billing</MenuItem>
													</Select>
												</FormControl>
												<TextField
													label={t('GENERAL.STREET_1')}
													size="small"
													value={addrForm.street1}
													error={Boolean(addrErrors.street1)}
													helperText={addrErrors.street1 || ""}
													onChange={e => {
														setAddrForm({ ...addrForm, street1: e.target.value });
														setAddrErrors(prev => ({ ...prev, street1: "" }));
														if (addrAlert) setAddrAlert("");
													}}
												/>
												<TextField label={t('GENERAL.STREET_2')} size="small" value={addrForm.street2} onChange={e => setAddrForm({ ...addrForm, street2: e.target.value })} />
												<TextField label={t('GENERAL.STREET_3')} size="small" value={addrForm.street3} onChange={e => setAddrForm({ ...addrForm, street3: e.target.value })} />
												<Box sx={{ display: 'flex', gap: 1 }}>
													<TextField
														label={t('LABELS.CITY')}
														size="small"
														fullWidth
														value={addrForm.city}
														error={Boolean(addrErrors.city)}
														helperText={addrErrors.city || ""}
														onChange={e => {
															setAddrForm({ ...addrForm, city: e.target.value });
															setAddrErrors(prev => ({ ...prev, city: "" }));
															if (addrAlert) setAddrAlert("");
														}}
													/>
													<TextField
														label="State"
														size="small"
														fullWidth
														value={addrForm.state}
														error={Boolean(addrErrors.state)}
														helperText={addrErrors.state || ""}
														onChange={e => {
															setAddrForm({ ...addrForm, state: e.target.value });
															setAddrErrors(prev => ({ ...prev, state: "" }));
															if (addrAlert) setAddrAlert("");
														}}
													/>
												</Box>
												<Box sx={{ display: 'flex', gap: 1 }}>
													<TextField
														label="Zip"
														size="small"
														fullWidth
														value={addrForm.zip}
														error={Boolean(addrErrors.zip)}
														helperText={addrErrors.zip || ""}
														onChange={e => {
															setAddrForm({ ...addrForm, zip: e.target.value });
															setAddrErrors(prev => ({ ...prev, zip: "" }));
															if (addrAlert) setAddrAlert("");
														}}
													/>
													<TextField
														label={t('LABELS.COUNTRY')}
														size="small"
														fullWidth
														value={addrForm.country}
														error={Boolean(addrErrors.country)}
														helperText={addrErrors.country || ""}
														onChange={e => {
															setAddrForm({ ...addrForm, country: e.target.value });
															setAddrErrors(prev => ({ ...prev, country: "" }));
															if (addrAlert) setAddrAlert("");
														}}
													/>
												</Box>
											</Box>
										)}
										actions={(
											<>
												<Button variant="outlined" onClick={() => setAddrModalOpen(false)}>{t('BUTTON.CANCEL')}</Button>
												<Button variant="contained" disabled={addrSaving} onClick={saveAddress}>{addrSaving ? 'Saving...' : 'Save'}</Button>
											</>
										)}
									/>
								)}
							</Box>
						)}

						{hasServiceDefinitions && tabIndex === 2 && (
							<Box sx={{ mt: 2, px: 2 }}>
								{servicesLoading ? (
									<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 160 }}>
										<CircularProgress />
									</Box>
								) : (
									<>
										<Box sx={{ display: 'flex', flexDirection: 'column' }}>
											{serviceOptions.map((service, index) => (
												<Box
													key={service.key}
													sx={{
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'space-between',
														gap: 2,
														py: 1,
														px: 0.5,
														borderBottom: index === serviceOptions.length - 1 ? 'none' : '1px solid',
														borderColor: 'divider',
													}}
												>
													<Box sx={{ mr: 2 }}>
														<Typography variant="subtitle1" fontWeight={600}>{t(service.label)}</Typography>
														{service.description && (
															<Typography variant="body2" color="text.secondary">
																{service.description}
															</Typography>
														)}
													</Box>
													<Switch
														color="primary"
														checked={Boolean(serviceMeta[service.key])}
														onChange={() => handleServiceToggle(service.key)}
														disabled={servicesSaving || servicesLoading}
													/>
												</Box>
											))}
										</Box>
										<Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 3, gap: 1 }}>
											{servicesError && (
												<Typography variant="body2" color="error" sx={{ mr: 'auto' }}>
													{servicesError}
												</Typography>
											)}
											<Button variant="contained" color="primary" onClick={handleSaveServices} disabled={servicesSaving}>
												{servicesSaving && <CircularProgress size={16} sx={{ color: 'white', mr: 1 }} />}
												{t('BUTTON.SAVE')}
											</Button>
										</Box>
									</>
								)}
							</Box>
						)}
					</>
				)}
			</Box>
			<ModalWrapper
				open={userModalState.open}
				setOpen={() => { }}
				onClose={handleCloseUserModal}
				title={
					userModalState.site
						? `${userModalState.site.customer_site_name || ""}  ${t(LABELS.USERS)}`
						: t(LABELS.USERS)
				}
				content={
					userModalState.loading ? (
						<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
							<CircularProgress />
						</Box>
					) : (
							<Datagrid
								data={userModalState.users}
								columns={userColumns}
								checkboxSelection={false}
								rowIdField="user_id"
								showSearchBar={true}
								hideFilterFromColumnMenu
								lockActiveColumnInManageColumns
								preventHidingLastColumn
							/>
					)
				}
				actions={
					<Button onClick={handleCloseUserModal}>{t('BUTTON.CLOSE') || 'Close'}</Button>
				}
			/>
		</div>
	);
};

export default ManageSites;
